import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

interface Thread {
  id: string;
  account_id: string;
  order_id: string | null;
  buyer_name: string | null;
  buyer_email: string | null;
  marketplace_id: string | null;
  product_title: string | null;
  asin: string | null;
  category: string;
  priority: string;
  status: string;
  language: string;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  notes: string | null;
}

const SC_DOMAINS: Record<string, string> = {
  ATVPDKIKX0DER: 'sellercentral.amazon.com',
  A2EUQ1WTGCTBG2: 'sellercentral.amazon.ca',
  A1AM78C64UM0Y8: 'sellercentral.amazon.com.mx',
  A1F83G8C2ARO7P: 'sellercentral.amazon.co.uk',
  A1PA6795UKMFR9: 'sellercentral.amazon.de',
  A13V1IB3VIYZZH: 'sellercentral.amazon.fr',
  APJ6JRA9NG5V4: 'sellercentral.amazon.it',
  A1RKKUPIHCS9HS: 'sellercentral.amazon.es',
  A1805IZSGTT6HS: 'sellercentral.amazon.nl',
  A2NODRKZP88ZB9: 'sellercentral.amazon.se',
};

function getSellerCentralMessageUrl(thread: Thread): string | null {
  if (!thread.order_id) return null;
  const domain = (thread.marketplace_id && SC_DOMAINS[thread.marketplace_id]) || 'sellercentral.amazon.com';
  return `https://${domain}/messaging/thread/${thread.order_id}`;
}

interface Message {
  id: string;
  direction: string;
  sender_type: string;
  subject: string | null;
  body: string;
  body_translated: string | null;
  original_language: string | null;
  ai_category: string | null;
  ai_sentiment: string | null;
  ai_draft_response: string | null;
  ai_draft_translated: string | null;
  status: string;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  pending_response: 'bg-yellow-100 text-yellow-800',
  auto_responded: 'bg-green-100 text-green-800',
  resolved: 'bg-gray-100 text-gray-600',
  escalated: 'bg-red-100 text-red-800',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const MP_FLAGS: Record<string, string> = {
  ATVPDKIKX0DER: '🇺🇸', A2EUQ1WTGCTBG2: '🇨🇦', A1AM78C64UM0Y8: '🇲🇽',
  A1F83G8C2ARO7P: '🇬🇧', A1PA6795UKMFR9: '🇩🇪', A13V1IB3VIYZZH: '🇫🇷',
  APJ6JRA9NG5V4: '🇮🇹', A1RKKUPIHCS9HS: '🇪🇸', A1805IZSGTT6HS: '🇳🇱', A2NODRKZP88ZB9: '🇸🇪',
};

const MP_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL', A2NODRKZP88ZB9: 'SE',
};

export function MessagesInbox() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [statusFilter, categoryFilter]);

  async function loadThreads() {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    const data = await api.get<Thread[]>(`/messages/threads?${params}`);
    setThreads(data);
    setLoading(false);
  }

  async function selectThread(thread: Thread) {
    setSelectedThread(thread);
    const data = await api.get<{ thread: Thread; messages: Message[] }>(`/messages/threads/${thread.id}`);
    setMessages(data.messages);
    setNotes(data.thread.notes ?? '');
    setNotesSaved(true);
  }

  async function saveNotes() {
    if (!selectedThread) return;
    setNotesSaving(true);
    await api.patch(`/messages/threads/${selectedThread.id}`, { notes });
    setNotesSaved(true);
    setNotesSaving(false);
  }

  async function sendReply() {
    if (!selectedThread || !replyText.trim()) return;
    setSending(true);
    await api.post(`/messages/threads/${selectedThread.id}/reply`, { body: replyText });
    setReplyText('');
    setSending(false);
    await selectThread(selectedThread);
  }

  async function approveDraft() {
    if (!selectedThread) return;
    setSending(true);
    await api.post(`/messages/threads/${selectedThread.id}/approve-draft`);
    setSending(false);
    await selectThread(selectedThread);
  }

  async function updateStatus(status: string) {
    if (!selectedThread) return;
    await api.patch(`/messages/threads/${selectedThread.id}`, { status });
    await loadThreads();
    setSelectedThread({ ...selectedThread, status });
  }

  const latestDraft = messages.find(m => m.ai_draft_response);

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* Thread List */}
      <div className="w-96 flex-shrink-0 overflow-y-auto rounded-xl border border-gray-200 bg-white">
        {/* Filters */}
        <div className="sticky top-0 border-b border-gray-100 bg-white p-3 space-y-2">
          <h2 className="text-sm font-semibold">Buyer Messages</h2>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="pending_response">Pending</option>
              <option value="auto_responded">Auto-responded</option>
              <option value="resolved">Resolved</option>
              <option value="escalated">Escalated</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="">All Categories</option>
              <option value="shipping_inquiry">Shipping</option>
              <option value="refund_request">Refund</option>
              <option value="product_question">Product Q</option>
              <option value="complaint">Complaint</option>
              <option value="feedback">Feedback</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-400">Loading...</div>
        ) : threads.length === 0 ? (
          <div className="p-4 text-sm text-gray-400">No messages yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => selectThread(t)}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedThread?.id === t.id ? 'bg-brand-50 border-l-2 border-brand-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">
                    {t.marketplace_id && <span className="mr-1">{MP_FLAGS[t.marketplace_id] ?? ''}</span>}
                    {t.buyer_name ?? 'Unknown'}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[t.status] ?? 'bg-gray-100'}`}>
                    {t.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-500 truncate">
                  {t.marketplace_id && <span className="text-gray-400">{MP_LABELS[t.marketplace_id] ?? ''} &middot; </span>}
                  {t.order_id ? `#${t.order_id}` : 'No order'} &middot; {t.category.replace('_', ' ')}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-400">
                  <span>{t.language.toUpperCase()}</span>
                  <span>&middot;</span>
                  <span>{PRIORITY_LABELS[t.priority]}</span>
                  <span>&middot;</span>
                  <span>{t.message_count} msg</span>
                  {t.last_message_at && (
                    <>
                      <span>&middot;</span>
                      <span>{new Date(t.last_message_at).toLocaleDateString()}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversation View */}
      <div className="flex flex-1 flex-col rounded-xl border border-gray-200 bg-white">
        {!selectedThread ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
            Select a thread to view conversation
          </div>
        ) : (
          <>
            {/* Thread Header */}
            <div className="border-b border-gray-100 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">
                    {selectedThread.marketplace_id && <span className="mr-1">{MP_FLAGS[selectedThread.marketplace_id] ?? ''}</span>}
                    {selectedThread.buyer_name ?? 'Unknown Buyer'}
                    {selectedThread.marketplace_id && <span className="ml-2 text-xs font-normal text-gray-400">{MP_LABELS[selectedThread.marketplace_id] ?? ''}</span>}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Order: {selectedThread.order_id ?? 'N/A'} &middot;{' '}
                    {selectedThread.product_title ?? selectedThread.asin ?? 'N/A'}
                    {(() => {
                      const scUrl = getSellerCentralMessageUrl(selectedThread);
                      return scUrl ? (
                        <a href={scUrl} target="_blank" rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-800 hover:underline">
                          View in Seller Central &rarr;
                        </a>
                      ) : null;
                    })()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {selectedThread.status !== 'resolved' && (
                    <button
                      onClick={() => updateStatus('resolved')}
                      className="rounded-lg border border-green-200 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                    >
                      Resolve
                    </button>
                  )}
                  {selectedThread.status !== 'escalated' && (
                    <button
                      onClick={() => updateStatus('escalated')}
                      className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      Escalate
                    </button>
                  )}
                </div>
              </div>
              {/* Internal Notes */}
              <details className="group">
                <summary className="text-xs font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700">
                  Internal Notes {notes ? `(${notes.split('\n').filter(Boolean).length})` : ''}
                  {!notesSaved && <span className="ml-1 text-amber-500">*</span>}
                </summary>
                <div className="mt-2 flex gap-2">
                  <textarea
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); setNotesSaved(false); }}
                    onBlur={() => { if (!notesSaved) saveNotes(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveNotes(); } }}
                    placeholder="Add internal notes about this conversation..."
                    rows={2}
                    className="flex-1 rounded-lg border border-gray-200 bg-yellow-50/50 px-3 py-2 text-xs focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
                  />
                  <button
                    onClick={saveNotes}
                    disabled={notesSaved || notesSaving}
                    className="self-end rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-40 transition"
                  >
                    {notesSaving ? 'Saving...' : notesSaved ? 'Saved' : 'Save'}
                  </button>
                </div>
              </details>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-lg p-3 ${
                    m.direction === 'inbound'
                      ? 'bg-gray-50 border border-gray-100'
                      : 'bg-brand-50 border border-brand-100 ml-8'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600">
                      {m.direction === 'inbound' ? 'Buyer' : 'You'}{' '}
                      {m.sender_type === 'auto' && '(auto)'}{' '}
                      {m.original_language && m.original_language !== 'en' && (
                        <span className="text-gray-400">({m.original_language})</span>
                      )}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{m.body_translated ?? m.body}</p>
                  {m.body_translated && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer">Show original</summary>
                      <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap">{m.body}</p>
                    </details>
                  )}
                  {m.ai_sentiment && (
                    <div className="mt-2 flex gap-2 text-[10px]">
                      {m.ai_category && <span className="rounded bg-gray-100 px-1.5 py-0.5">{m.ai_category}</span>}
                      <span className="rounded bg-gray-100 px-1.5 py-0.5">{m.ai_sentiment}</span>
                    </div>
                  )}
                </div>
              ))}

              {/* AI Draft */}
              {latestDraft?.ai_draft_response && selectedThread.status !== 'resolved' && (
                <div className="rounded-lg border-2 border-dashed border-brand-200 bg-brand-50/50 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-brand-700">AI Draft Response</span>
                    <button
                      onClick={approveDraft}
                      disabled={sending}
                      className="rounded bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      {sending ? 'Sending...' : 'Send as-is'}
                    </button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">
                    {latestDraft.ai_draft_translated ?? latestDraft.ai_draft_response}
                  </p>
                  {latestDraft.ai_draft_translated && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer">
                        Draft in buyer's language
                      </summary>
                      <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap">
                        {latestDraft.ai_draft_response}
                      </p>
                    </details>
                  )}
                </div>
              )}
            </div>

            {/* Reply Box */}
            {selectedThread.status !== 'resolved' && (
              <div className="border-t border-gray-100 p-3">
                <div className="flex gap-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
                  />
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="self-end rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {sending ? '...' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
